import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useOrg } from '../contexts/OrgContext';
import { useSeatUsage } from '../components/SeatLimitBanner';
import { paymentAPI, publicAPI } from '../services/api';
import { calculateGST } from '../utils/gst';
import {
  HiCheck,
  HiMinusSm,
  HiPlusSm,
  HiShieldCheck,
  HiTag,
  HiX,
  HiOutlineGift,
} from 'react-icons/hi';
import toast from 'react-hot-toast';

// Lazy-load Razorpay checkout SDK
let razorpayLoadPromise = null;
function loadRazorpaySDK() {
  if (window.Razorpay) return Promise.resolve();
  if (razorpayLoadPromise) return razorpayLoadPromise;
  razorpayLoadPromise = new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.async = true;
    script.onload = resolve;
    script.onerror = () => { razorpayLoadPromise = null; reject(new Error('Failed to load Razorpay SDK')); };
    document.body.appendChild(script);
  });
  return razorpayLoadPromise;
}

const PRICE_PER_USER = { monthly: 599, yearly: 299 };

const CYCLES = [
  { id: 'monthly', label: 'Monthly', backend: 'monthly' },
  { id: 'yearly', label: 'Yearly', backend: 'yearly' },
];

const inr = (n) => `₹${n.toLocaleString('en-IN')}`;

const Checkout = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { organization, refreshOrg, paidSeats } = useOrg();
  const { activeTelecallers } = useSeatUsage();

  const planParam = searchParams.get('plan') || 'full-access';
  const cycleParam = searchParams.get('cycle') || 'monthly';
  const promoParam = searchParams.get('promo') || '';

  const usersParam = parseInt(searchParams.get('users'), 10);
  const [cycleId, setCycleId] = useState(CYCLES.find((c) => c.id === cycleParam)?.id || 'monthly');
  const [numUsers, setNumUsers] = useState(usersParam > 0 ? Math.min(usersParam, 100) : 1);
  const [seatsBumped, setSeatsBumped] = useState(false);

  // Default the telecaller count to at least the current paid seats and the number of
  // active telecallers, so an org that is already over its seat limit doesn't buy too few.
  const seatDefaultApplied = useRef(false);
  useEffect(() => {
    if (seatDefaultApplied.current) return;
    if (activeTelecallers == null && !(usersParam > 0) && !organization) return;
    const current = usersParam > 0 ? usersParam : (organization ? paidSeats : 1);
    const minSeats = Math.min(Math.max(current || 1, activeTelecallers || 0, 1), 100);
    if (activeTelecallers != null) seatDefaultApplied.current = true;
    if (minSeats > numUsers) {
      setNumUsers(minSeats);
      if (activeTelecallers != null && activeTelecallers > (current || 1)) setSeatsBumped(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTelecallers, organization, paidSeats, usersParam]);
  const [serverPlans, setServerPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);

  // Promo code state
  const [promoCode, setPromoCode] = useState(promoParam);
  const [promoResult, setPromoResult] = useState(null);
  const [promoLoading, setPromoLoading] = useState(false);
  const [promoError, setPromoError] = useState('');

  // Refer & Earn coin redemption state
  const [useCoins, setUseCoins] = useState(false);
  const [coinsToRedeem, setCoinsToRedeem] = useState(0);

  const cycle = CYCLES.find((c) => c.id === cycleId);

  // Fetch plans
  useEffect(() => {
    const fetchPlans = async () => {
      try {
        const res = await paymentAPI.getPlans();
        setServerPlans(res.data?.data || []);
      } catch (err) {
        console.error('Failed to fetch plans:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchPlans();
  }, []);

  // Find the selected plan
  const selectedPlan = useMemo(() => {
    return serverPlans.find((p) => p.slug === planParam || p._id === planParam)
      || serverPlans.find((p) => p.slug === 'full-access');
  }, [serverPlans, planParam]);

  const isFreePlan = selectedPlan?.slug === 'free';
  const perUserPrice = selectedPlan?.isPerUser
    ? (selectedPlan.pricePerUser?.[cycle.backend] || PRICE_PER_USER[cycleId])
    : 0;
  const flatPrice = selectedPlan && !selectedPlan.isPerUser
    ? (selectedPlan.price?.[cycle.backend] || 0)
    : 0;

  // Pricing calculations
  const baseAmount = selectedPlan?.isPerUser
    ? perUserPrice * numUsers * (cycleId === 'yearly' ? 12 : 1)
    : flatPrice;

  const discountAmount = promoResult?.discountAmount || 0;

  // Refer & Earn: coins can only offset what's left after the promo discount
  const coinValueInRupees = organization?.referAndEarn?.coinValueInRupees || 1;
  const coinBalance = organization?.coinBalance || 0;
  const remainingAfterPromo = Math.max(0, baseAmount - discountAmount);
  const maxRedeemableCoins = Math.min(coinBalance, Math.floor(remainingAfterPromo / coinValueInRupees));
  const appliedCoins = useCoins ? Math.min(coinsToRedeem, maxRedeemableCoins) : 0;
  const coinDiscountAmount = Math.round(appliedCoins * coinValueInRupees * 100) / 100;

  const discountedBase = Math.round((baseAmount - discountAmount - coinDiscountAmount) * 100) / 100;
  const gstConfig = selectedPlan?.gstConfig || 'applicable';
  const orgState = organization?.address?.state || null;

  // Default to redeeming the max usable coins as soon as "Use my coins" is toggled on,
  // and keep it clamped if the promo discount changes how much room is left to redeem.
  useEffect(() => {
    if (useCoins) {
      setCoinsToRedeem((prev) => (prev > 0 ? Math.min(prev, maxRedeemableCoins) : maxRedeemableCoins));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [useCoins, maxRedeemableCoins]);
  const { gstAmount, cgstAmount, sgstAmount, totalAmount, gstLabel, gstType } = calculateGST(discountedBase, gstConfig, orgState);

  // Apply promo code
  const handleApplyPromo = async () => {
    if (!promoCode.trim()) return;
    setPromoLoading(true);
    setPromoError('');
    setPromoResult(null);

    try {
      const res = await paymentAPI.validatePromo({
        code: promoCode.trim(),
        planId: selectedPlan?.slug || planParam,
        billingCycle: cycle.backend,
        numUsers,
      });
      if (res.data?.success && res.data?.data?.valid) {
        setPromoResult(res.data.data);
        setPromoError('');
        toast.success(`Promo code "${res.data.data.code}" applied! ${res.data.data.discountType === 'percentage' ? `${res.data.data.discountValue}% off` : `${inr(res.data.data.discountValue)} off`} — You save ${inr(res.data.data.discountAmount)}`);
      }
    } catch (err) {
      const msg = err.response?.data?.message || 'Invalid promo code';
      setPromoError(msg);
      setPromoResult(null);
    } finally {
      setPromoLoading(false);
    }
  };

  const handleRemovePromo = () => {
    setPromoCode('');
    setPromoResult(null);
    setPromoError('');
  };

  // Handle free activation (100% discount — zero amount, bypasses Razorpay)
  const handleFreeActivation = async () => {
    setProcessing(true);
    try {
      const planId = selectedPlan?._id || selectedPlan?.slug || planParam;

      const res = await paymentAPI.activateFree({
        planId,
        billingCycle: cycle.backend,
        numUsers,
        promoCode: promoResult?.code,
        coinsToRedeem: appliedCoins || undefined,
      });

      if (res.data?.success) {
        toast.success('Subscription activated successfully! No payment required — 100% discount applied.');
        await refreshOrg();
        window.dispatchEvent(new CustomEvent('subscriptionUpdated'));
        navigate('/app/settings?tab=subscription');
      }
    } catch (err) {
      console.error('Free activation error:', err);
      toast.error(err.response?.data?.message || 'Failed to activate subscription. Please try again.');
    } finally {
      setProcessing(false);
    }
  };

  // Handle payment
  const handlePayment = async () => {
    if (isFreePlan) return;

    // If total is zero (100% discount), bypass Razorpay entirely
    if (totalAmount === 0) {
      return handleFreeActivation();
    }

    setProcessing(true);
    try {
      await loadRazorpaySDK();

      const planId = selectedPlan?._id || selectedPlan?.slug || planParam;

      const { data: order } = await paymentAPI.createOrder({
        planId,
        billingCycle: cycle.backend,
        numUsers,
        promoCode: promoResult?.code || undefined,
        coinsToRedeem: appliedCoins || undefined,
      });

      const options = {
        key: order.keyId,
        amount: order.amount,
        currency: order.currency,
        name: 'TeleValley',
        description: `${selectedPlan?.name || 'Full Access'} — ${numUsers} telecaller${numUsers > 1 ? 's' : ''} + 1 admin · ${order.cycleLabel}`,
        order_id: order.orderId,
        handler: async (response) => {
          try {
            const verifyRes = await paymentAPI.verifyPayment({
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
              planId,
              billingCycle: cycle.backend,
              numUsers,
            });

            if (verifyRes.data?.success) {
              toast.success('Subscription activated successfully!');
              await refreshOrg();
              window.dispatchEvent(new CustomEvent('subscriptionUpdated'));
              navigate('/app/settings?tab=subscription');
            }
          } catch (verifyErr) {
            console.error('Payment verification error:', verifyErr);
            toast.error(verifyErr.response?.data?.message || 'Payment verification failed. Please contact support.');
          }
        },
        prefill: {
          name: organization?.name || '',
          email: organization?.email || '',
        },
        theme: { color: '#064ee6' },
        modal: {
          ondismiss: () => {
            toast('Payment cancelled', { icon: 'ℹ️' });
          },
        },
      };

      const rzp = new window.Razorpay(options);
      rzp.on('payment.failed', (resp) => {
        toast.error(`Payment failed: ${resp.error.description}`);
      });
      rzp.open();
    } catch (err) {
      console.error('Create order error:', err);
      // If the backend says the amount is zero (100% discount), use free activation
      if (err.response?.data?.zeroAmount) {
        return handleFreeActivation();
      }
      toast.error(err.response?.data?.message || 'Failed to initiate payment. Please try again.');
    } finally {
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-2xl font-bold text-gray-900">Checkout</h1>
          <p className="mt-1 text-sm text-gray-500">Complete your subscription to unlock all features</p>
        </div>

        {/* Order Summary */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 mb-4">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Order Summary</h2>

          {/* Plan info */}
          <div className="flex items-start justify-between mb-4">
            <div>
              <p className="font-semibold text-gray-900">{selectedPlan?.name || 'Full Access Plan'}</p>
              <p className="text-sm text-gray-500">
                {selectedPlan?.isPerUser ? 'Per-user pricing' : 'Flat pricing'}
                {selectedPlan?.isPerUser && ` · ${numUsers} telecaller${numUsers > 1 ? 's' : ''} + 1 admin`}
              </p>
            </div>
            <div className="text-right">
              <p className="font-semibold text-gray-900">
                {isFreePlan ? 'Free' : selectedPlan?.isPerUser
                  ? `${inr(perUserPrice)}/telecaller/mo`
                  : `${inr(flatPrice)}/mo`}
              </p>
            </div>
          </div>

          {/* Billing Cycle Toggle */}
          {!isFreePlan && (
            <div className="mb-4">
              <label className="text-sm font-medium text-gray-700 block mb-2">Billing Cycle</label>
              <div className="inline-flex rounded-lg border border-gray-200 bg-gray-50 p-0.5">
                {CYCLES.map((c) => (
                  <button
                    key={c.id}
                    onClick={() => { setCycleId(c.id); setPromoResult(null); }}
                    className={`rounded-md px-4 py-2 text-sm font-semibold transition-all ${
                      cycleId === c.id ? 'bg-primary-600 text-white shadow-sm' : 'text-gray-600 hover:text-primary-600'
                    }`}
                  >
                    {c.label}
                    {c.id === 'yearly' && ' (Save 50%)'}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* User count for per-user plans */}
          {!isFreePlan && selectedPlan?.isPerUser && (
            <div className="mb-4">
              <label className="text-sm font-medium text-gray-700">Number of Telecallers</label>
              <div className="mt-2 flex items-center gap-3">
                <button
                  onClick={() => setNumUsers(Math.max(1, numUsers - 1))}
                  disabled={numUsers <= 1}
                  className="flex h-10 w-10 items-center justify-center rounded-lg border border-gray-300 bg-white text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <HiMinusSm className="h-4 w-4" />
                </button>
                <input
                  type="number"
                  min={1}
                  max={100}
                  value={numUsers}
                  onChange={(e) => {
                    const val = parseInt(e.target.value, 10);
                    if (!isNaN(val)) {
                      setNumUsers(Math.min(100, Math.max(1, val)));
                    }
                  }}
                  onBlur={() => {
                    if (!numUsers || numUsers < 1) setNumUsers(1);
                    if (numUsers > 100) setNumUsers(100);
                  }}
                  className="flex h-10 w-16 items-center justify-center rounded-lg border border-gray-300 bg-white text-center text-lg font-bold text-gray-900 focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 focus:outline-none transition-colors [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                />
                <button
                  onClick={() => setNumUsers(Math.min(100, numUsers + 1))}
                  disabled={numUsers >= 100}
                  className="flex h-10 w-10 items-center justify-center rounded-lg border border-gray-300 bg-white text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <HiPlusSm className="h-4 w-4" />
                </button>
                <span className="text-sm text-gray-500">telecaller{numUsers > 1 ? 's' : ''} + 1 admin</span>
              </div>
              {seatsBumped && (
                <p className="mt-2 text-xs text-amber-700">
                  Set to {activeTelecallers} to cover your currently active telecallers.
                </p>
              )}
            </div>
          )}

          {/* Pricing Breakdown */}
          {!isFreePlan && (
            <div className="border-t border-gray-200 pt-4 space-y-2">
              <div className="flex justify-between text-sm text-gray-600">
                <span>
                  {selectedPlan?.isPerUser
                    ? `${inr(perUserPrice)} × ${numUsers} telecaller${numUsers > 1 ? 's' : ''} × ${cycleId === 'yearly' ? '12 months' : '1 month'}`
                    : `${selectedPlan?.name || 'Plan'} (${cycle.label})`}
                </span>
                <span>{inr(baseAmount)}</span>
              </div>

              {discountAmount > 0 && (
                <div className="flex justify-between text-sm text-green-600">
                  <span className="flex items-center gap-1">
                    <HiTag className="h-3.5 w-3.5" />
                    Promo: {promoResult?.code}
                    {promoResult?.discountType === 'percentage' && ` (${promoResult.discountValue}% off)`}
                  </span>
                  <span>−{inr(discountAmount)}</span>
                </div>
              )}

              {coinDiscountAmount > 0 && (
                <div className="flex justify-between text-sm text-green-600">
                  <span className="flex items-center gap-1">
                    <HiOutlineGift className="h-3.5 w-3.5" />
                    Coins: {appliedCoins.toLocaleString('en-IN')}
                  </span>
                  <span>−{inr(coinDiscountAmount)}</span>
                </div>
              )}

              <div className="flex justify-between text-sm text-gray-600">
                <span>Subtotal{(discountAmount > 0 || coinDiscountAmount > 0) ? ' (after discount)' : ''}</span>
                <span>{inr(discountedBase)}</span>
              </div>

              {gstConfig === 'none' ? null : gstType === 'cgst_sgst' ? (
                <>
                  <div className="flex justify-between text-sm text-gray-600">
                    <span>CGST (9%)</span>
                    <span>{inr(cgstAmount)}</span>
                  </div>
                  <div className="flex justify-between text-sm text-gray-600">
                    <span>SGST (9%)</span>
                    <span>{inr(sgstAmount)}</span>
                  </div>
                </>
              ) : (
                <div className="flex justify-between text-sm text-gray-600">
                  <span>IGST (18%)</span>
                  <span>{inr(gstAmount)}</span>
                </div>
              )}

              <div className="flex justify-between text-base font-bold text-gray-900 pt-2 border-t border-gray-200">
                <span>Total</span>
                <span>{inr(totalAmount)}</span>
              </div>

              {cycleId === 'yearly' && (
                <p className="text-xs text-gray-500 text-right">
                  Billed annually · {inr(perUserPrice)}/telecaller/mo
                </p>
              )}
            </div>
          )}
        </div>

        {/* Promo Code Section */}
        {!isFreePlan && (
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 mb-4">
            <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
              <HiTag className="h-4 w-4 text-primary-600" />
              Have a promo code?
            </h3>
            {promoResult ? (
              <div className="flex items-center justify-between bg-green-50 border border-green-200 rounded-lg px-4 py-3">
                <div>
                  <p className="font-semibold text-green-700">{promoResult.code}</p>
                  <p className="text-xs text-green-600">
                    {promoResult.discountType === 'percentage'
                      ? `${promoResult.discountValue}% discount applied`
                      : `${inr(promoResult.discountValue)} discount applied`}
                    {promoResult.discountType === 'percentage' && promoResult.maxDiscount && ` (max ${inr(promoResult.maxDiscount)})`}
                  </p>
                </div>
                <button onClick={handleRemovePromo} className="text-green-700 hover:text-green-900">
                  <HiX className="h-5 w-5" />
                </button>
              </div>
            ) : (
              <div className="flex gap-2">
                <input
                  type="text"
                  value={promoCode}
                  onChange={(e) => { setPromoCode(e.target.value.toUpperCase()); setPromoError(''); }}
                  placeholder="Enter promo code"
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                  onKeyDown={(e) => { if (e.key === 'Enter') handleApplyPromo(); }}
                />
                <button
                  onClick={handleApplyPromo}
                  disabled={promoLoading || !promoCode.trim()}
                  className="px-4 py-2 bg-primary-600 text-white text-sm font-medium rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {promoLoading ? 'Applying...' : 'Apply'}
                </button>
              </div>
            )}
            {promoError && (
              <p className="mt-2 text-xs text-red-600">{promoError}</p>
            )}
          </div>
        )}

        {/* Refer & Earn — Use Coins Section */}
        {!isFreePlan && coinBalance > 0 && (
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 mb-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                <HiOutlineGift className="h-4 w-4 text-primary-600" />
                Use your coins
              </h3>
              <label className="inline-flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={useCoins}
                  onChange={(e) => setUseCoins(e.target.checked)}
                  className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                <span className="text-xs font-medium text-gray-600">Apply</span>
              </label>
            </div>
            <p className="text-xs text-gray-500 mb-3">
              You have {coinBalance.toLocaleString('en-IN')} coins (≈ {inr(coinBalance * coinValueInRupees)}).
              {maxRedeemableCoins < coinBalance && ' Only part of your balance is needed for this bill.'}
            </p>
            {useCoins && (
              <div className="flex items-center gap-3">
                <input
                  type="range"
                  min="0"
                  max={maxRedeemableCoins}
                  value={Math.min(coinsToRedeem, maxRedeemableCoins)}
                  onChange={(e) => setCoinsToRedeem(parseInt(e.target.value, 10) || 0)}
                  className="flex-1 accent-primary-600"
                />
                <span className="text-sm font-semibold text-gray-900 whitespace-nowrap">
                  {appliedCoins.toLocaleString('en-IN')} coins (−{inr(coinDiscountAmount)})
                </span>
              </div>
            )}
          </div>
        )}

        {/* Pay Button */}
        {!isFreePlan && (
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 mb-4">
            <button
              onClick={handlePayment}
              disabled={processing}
              className="w-full py-3.5 bg-primary-600 text-white font-semibold rounded-lg hover:bg-primary-700 disabled:bg-gray-300 disabled:text-gray-500 disabled:cursor-not-allowed transition-colors"
            >
              {processing ? (
                <span className="inline-flex items-center gap-2">
                  <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  {totalAmount === 0 ? 'Activating...' : 'Processing...'}
                </span>
              ) : (
                totalAmount === 0
                  ? 'Activate Subscription — Free'
                  : `Pay ${inr(totalAmount)} · ${cycle.label}`
              )}
            </button>
            <p className="mt-3 text-xs text-gray-400 text-center flex items-center justify-center gap-1">
              <HiShieldCheck className="h-3.5 w-3.5 text-green-500" />
              {totalAmount === 0
                ? 'Free activation — no payment required · GST invoice included · Cancel anytime'
                : 'Secure checkout via Razorpay · GST invoice included · Cancel anytime'}
            </p>
          </div>
        )}

        {/* Features list */}
        {selectedPlan && (
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
            <h3 className="text-sm font-semibold text-gray-900 mb-3">What's included</h3>
            <ul className="grid gap-x-4 gap-y-2 sm:grid-cols-2">
              {(selectedPlan.slug === 'free' ? [
                'SIM Based Calling',
                'Lead Management (up to 150)',
                'Follow-Up Reminders',
                'Basic Reports',
                'Notes & Activity Tracking',
                'Mobile App Access',
              ] : [
                'SIM Based Calling & Auto Dialer',
                'Call Recording',
                'Unlimited Lead Management',
                'Follow-Up Reminders',
                'Advanced Reports & Analytics',
                'Notes & Activity Tracking',
                'Team Performance Tracking',
                'Lead Distribution System',
                'Manager Dashboard & Monitoring',
                'WhatsApp & Email Integration',
                'Google Sheet Integration',
                'Mobile App Access',
                'Priority Support',
                'Data Export & Audit Logs',
              ]).map((f) => (
                <li key={f} className="flex items-start gap-2 text-sm text-gray-700">
                  <HiCheck className="mt-0.5 h-4 w-4 shrink-0 text-green-500" />
                  {f}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
};

export default Checkout;